/* K2 separate 360° viewpoints. Pannellum is loaded only on explicit activation. */
(() => {
  'use strict';
  if (customElements.get('k2-panorama')) return;
  const assets = new Map();
  let nextId = 0;
  const text = {
    en: {
      title: '360° views', close: 'Close 360° views', choose: 'Choose a viewpoint',
      help: 'Drag to look around, or focus the image and use the arrow keys. Use + and − to zoom. Escape closes the viewer. Each view is a separate photograph.',
      loading: 'Loading viewpoint…', ready: 'Viewpoint ready:', error: 'This 360° view could not load. You can retry or close the viewer to see the photos.',
      retry: 'Try again', controls: 'View controls', left: 'Look left', right: 'Look right',
      up: 'Look up', down: 'Look down', zoomIn: 'Zoom in', zoomOut: 'Zoom out', reset: 'Reset view',
      image: 'Interactive 360° image', still: 'Still preview of', unavailable: '360° viewing is unavailable. The photos remain available.'
    },
    fr: {
      title: 'Vues à 360°', close: 'Fermer les vues à 360°', choose: 'Choisir un point de vue',
      help: 'Faites glisser pour regarder autour, ou sélectionnez l’image et utilisez les flèches du clavier. Utilisez + et − pour zoomer. Échap ferme la fenêtre. Chaque vue est une photographie distincte.',
      loading: 'Chargement du point de vue…', ready: 'Point de vue prêt :', error: 'Cette vue à 360° n’a pas pu être chargée. Réessayez ou fermez la fenêtre pour voir les photos.',
      retry: 'Réessayer', controls: 'Commandes de la vue', left: 'Regarder à gauche', right: 'Regarder à droite',
      up: 'Regarder en haut', down: 'Regarder en bas', zoomIn: 'Zoom avant', zoomOut: 'Zoom arrière', reset: 'Réinitialiser',
      image: 'Image interactive à 360°', still: 'Aperçu fixe de', unavailable: 'La vue à 360° est indisponible. Les photos restent accessibles.'
    }
  };

  // Theme assets and store-hosted media only; no third-party viewer or config service.
  function localURL(value) {
    if (typeof value !== 'string' || !value.trim()) throw new Error('Missing asset URL');
    const url = new URL(value, document.baseURI);
    if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password ||
        (url.origin !== location.origin && !(url.protocol === 'https:' && url.hostname === 'cdn.shopify.com'))) {
      throw new Error('Asset URL must belong to this store or cdn.shopify.com');
    }
    return url.href;
  }
  function element(tag, className, content) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (content) node.textContent = content;
    return node;
  }
  function button(label, className) {
    const node = element('button', className, label);
    node.type = 'button';
    return node;
  }
  function loadAsset(url, kind) {
    const key = `${kind}:${url}`;
    if (assets.has(key)) return assets.get(key);
    const promise = new Promise((resolve, reject) => {
      const node = element(kind === 'js' ? 'script' : 'link');
      let settled = false;
      const finish = (error) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        node.onload = node.onerror = null;
        if (error) { node.remove(); assets.delete(key); reject(error); }
        else resolve();
      };
      const timer = setTimeout(() => finish(new Error('Viewer asset timeout')), 15000);
      node.onload = () => finish();
      node.onerror = () => finish(new Error('Viewer asset unavailable'));
      if (kind === 'js') { node.src = url; node.async = true; }
      else { node.rel = 'stylesheet'; node.href = url; }
      document.head.append(node);
    });
    assets.set(key, promise);
    return promise;
  }

  class K2Panorama extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.sequence = 0;
      this.words = text[(this.lang || document.documentElement.lang).toLowerCase().startsWith('fr') ? 'fr' : 'en'];
      this.openButton = this.querySelector('[data-panorama-open]');
      if (!this.openButton) return;
      this.openButton.hidden = true;
      try {
        if (typeof HTMLDialogElement === 'undefined' || !HTMLDialogElement.prototype.showModal) return;
        const raw = JSON.parse(this.querySelector('[data-panorama-scenes]').textContent);
        if (!Array.isArray(raw) || !raw.length || raw.length > 20) throw new Error('Invalid scene count');
        const ids = new Set();
        this.scenes = raw.map((scene) => {
          if (!scene || typeof scene.id !== 'string' || !/^[a-zA-Z0-9_-]{1,60}$/.test(scene.id) || ids.has(scene.id) ||
              typeof scene.label !== 'string' || !scene.label.trim() || scene.label.length > 180) throw new Error('Invalid scene');
          ids.add(scene.id);
          return { id: scene.id, label: scene.label, url: localURL(scene.url),
            preview: scene.preview ? localURL(scene.preview) : null,
            yaw: Number.isFinite(scene.yaw) ? Math.max(-180, Math.min(180, scene.yaw)) : 0,
            pitch: Number.isFinite(scene.pitch) ? Math.max(-85, Math.min(85, scene.pitch)) : 0,
            hfov: Number.isFinite(scene.hfov) ? Math.max(50, Math.min(110, scene.hfov)) : 95 };
        });
        this.libraryJS = localURL(this.dataset.libraryJs);
        this.libraryCSS = localURL(this.dataset.libraryCss);
        this.boundOpen = () => this.open();
        this.openButton.addEventListener('click', this.boundOpen);
        this.openButton.setAttribute('aria-haspopup', 'dialog');
        this.openButton.hidden = false;
      } catch (_error) {
        // Static content remains readable if the theme data is incomplete or invalid.
        const notice = element('p', 'k2-panorama__unavailable', this.words.unavailable);
        this.append(notice);
      }
    }

    disconnectedCallback() {
      this.close(false);
      this.initialized = false;
      this.dialog?.remove();
      this.dialog = null;
      if (this.openButton && this.boundOpen) this.openButton.removeEventListener('click', this.boundOpen);
    }

    buildDialog() {
      const id = `k2-panorama-${++nextId}`;
      this.dialog = element('dialog', 'k2-panorama__dialog');
      this.dialog.setAttribute('aria-labelledby', `${id}-title`);
      this.dialog.setAttribute('aria-describedby', `${id}-help`);
      this.dialog.addEventListener('cancel', (event) => { event.preventDefault(); this.close(); });
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.cleanup(); });
      const header = element('div', 'k2-panorama__header');
      const title = element('h2', 'k2-panorama__title', this.dataset.title || this.words.title);
      title.id = `${id}-title`;
      this.closeButton = button(this.words.close, 'k2-panorama__button k2-panorama__close');
      this.closeButton.addEventListener('click', () => this.close());
      header.append(title, this.closeButton);
      const help = element('p', 'k2-panorama__help', this.words.help);
      help.id = `${id}-help`;
      const choices = element('div', 'k2-panorama__choices');
      choices.setAttribute('role', 'group');
      choices.setAttribute('aria-label', this.words.choose);
      this.sceneButtons = this.scenes.map((scene, index) => {
        const choice = button(scene.label, 'k2-panorama__button');
        choice.setAttribute('aria-pressed', 'false');
        choice.addEventListener('click', () => this.select(index));
        choices.append(choice);
        return choice;
      });
      this.frame = element('div', 'k2-panorama__frame');
      this.preview = element('img', 'k2-panorama__preview');
      this.preview.alt = '';
      this.preview.hidden = true;
      this.preview.addEventListener('error', () => { this.previewFailed = true; this.preview.hidden = true; });
      this.mount = element('div', 'k2-panorama__canvas');
      this.mount.tabIndex = 0;
      this.mount.setAttribute('role', 'group');
      this.mount.setAttribute('aria-label', this.words.image);
      this.mount.setAttribute('aria-describedby', help.id);
      this.mount.addEventListener('keydown', (event) => {
        const actions = { ArrowLeft: 'left', ArrowRight: 'right', ArrowUp: 'up', ArrowDown: 'down',
          '+': 'zoomIn', '=': 'zoomIn', '-': 'zoomOut', '_': 'zoomOut', Home: 'reset' };
        if (actions[event.key] && !event.altKey && !event.ctrlKey && !event.metaKey) {
          event.preventDefault();
          this.control(actions[event.key]);
        }
      });
      this.frame.append(this.preview, this.mount);
      const controls = element('div', 'k2-panorama__controls');
      controls.setAttribute('role', 'group');
      controls.setAttribute('aria-label', this.words.controls);
      this.controlButtons = ['left', 'right', 'up', 'down', 'zoomIn', 'zoomOut', 'reset'].map((action) => {
        const control = button(this.words[action], 'k2-panorama__button');
        control.disabled = true;
        control.addEventListener('click', () => this.control(action));
        controls.append(control);
        return control;
      });
      this.status = element('p', 'k2-panorama__status');
      this.status.setAttribute('role', 'status');
      this.status.setAttribute('aria-live', 'polite');
      this.status.setAttribute('aria-atomic', 'true');
      this.retry = button(this.words.retry, 'k2-panorama__button k2-panorama__retry');
      this.retry.hidden = true;
      this.retry.addEventListener('click', () => this.select(this.currentIndex));
      this.dialog.append(header, help, choices, this.frame, controls, this.status, this.retry);
      this.append(this.dialog);
    }

    open() {
      if (!this.scenes?.length || this.dialog?.open) return;
      if (!this.dialog) this.buildDialog();
      this.returnFocus = this.openButton;
      this.dialog.showModal();
      this.closeButton.focus();
      this.select(this.currentIndex || 0);
    }

    async select(index) {
      if (!this.dialog?.open) return;
      this.cleanup();
      const token = this.sequence;
      const scene = this.scenes[index];
      this.currentIndex = index;
      this.sceneButtons.forEach((item, i) => item.setAttribute('aria-pressed', String(i === index)));
      this.status.textContent = this.words.loading;
      this.frame.setAttribute('aria-busy', 'true');
      this.frame.classList.remove('is-ready');
      this.mount.tabIndex = -1;
      this.retry.hidden = true;
      this.controlButtons.forEach((item) => { item.disabled = true; });
      this.previewFailed = false;
      this.preview.hidden = !scene.preview;
      if (scene.preview) { this.preview.alt = `${this.words.still} ${scene.label}`; this.preview.src = scene.preview; }
      else this.preview.removeAttribute('src');
      const active = () => token === this.sequence && this.dialog?.open && this.isConnected;
      const controller = new AbortController();
      this.request = controller;
      const timeout = setTimeout(() => controller.abort(), 30000);
      try {
        await Promise.all([loadAsset(this.libraryCSS, 'css'), loadAsset(this.libraryJS, 'js')]);
        if (!active()) return;
        if (!window.pannellum?.viewer) throw new Error('Viewer unavailable');
        const response = await fetch(scene.url, { signal: controller.signal, credentials: 'omit', redirect: 'error' });
        if (!response.ok || !response.headers.get('content-type')?.startsWith('image/')) throw new Error('Image unavailable');
        const blob = await response.blob();
        if (!active()) return;
        if (blob.size > 50 * 1024 * 1024) throw new Error('Image is too large');
        const objectURL = URL.createObjectURL(blob);
        this.objectURL = objectURL;
        const photo = new Image();
        photo.src = objectURL;
        await photo.decode();
        if (!active()) return;
        if (!photo.naturalWidth || photo.naturalWidth !== photo.naturalHeight * 2 || photo.naturalWidth > 8192) {
          throw new Error('A complete 2:1 panorama up to 8192 pixels wide is required');
        }
        clearTimeout(timeout);
        // Use an already-decoded image so close / scene changes can abort network work.
        // Pannellum's image-source mode is initialized with setUpdate(false): no video loop.
        const viewer = window.pannellum.viewer(this.mount, {
          type: 'equirectangular', panorama: photo, dynamic: true, dynamicUpdate: false, autoLoad: true,
          showControls: false, compass: false, autoRotate: 0, orientationOnByDefault: false,
          disableKeyboardCtrl: true, keyboardZoom: false, mouseZoom: false, draggable: true,
          doubleClickZoom: false, friction: 1, escapeHTML: true, ignoreGPanoXMP: true,
          pitch: scene.pitch, yaw: scene.yaw, hfov: scene.hfov, minHfov: 40, maxHfov: 110,
          strings: { loadingLabel: this.words.loading, noPanoramaError: this.words.error,
            genericWebGLError: this.words.error, webglError: this.words.error, unknownError: this.words.error }
        });
        this.viewer = viewer;
        viewer.on('load', () => {
          if (!active()) return;
          clearTimeout(this.renderTimer);
          this.frame.classList.add('is-ready');
          this.frame.setAttribute('aria-busy', 'false');
          this.mount.tabIndex = 0;
          this.preview.hidden = true;
          this.controlButtons.forEach((item) => { item.disabled = false; });
          this.status.textContent = `${this.words.ready} ${scene.label}`;
          this.dispatchEvent(new CustomEvent('k2-panorama:ready', { bubbles: true, detail: { sceneId: scene.id } }));
        });
        viewer.on('error', () => { if (active()) this.fail(); });
        this.renderTimer = setTimeout(() => { if (active()) this.fail(); }, 10000);
        viewer.setUpdate(false);
      } catch (_error) {
        if (active()) this.fail();
      } finally {
        clearTimeout(timeout);
      }
    }

    control(action) {
      if (!this.viewer?.isLoaded()) return;
      const viewer = this.viewer;
      if (action === 'left') viewer.setYaw(viewer.getYaw() - 12, false);
      if (action === 'right') viewer.setYaw(viewer.getYaw() + 12, false);
      if (action === 'up') viewer.setPitch(Math.min(85, viewer.getPitch() + 8), false);
      if (action === 'down') viewer.setPitch(Math.max(-85, viewer.getPitch() - 8), false);
      if (action === 'zoomIn') viewer.setHfov(Math.max(40, viewer.getHfov() - 10), false);
      if (action === 'zoomOut') viewer.setHfov(Math.min(110, viewer.getHfov() + 10), false);
      if (action === 'reset') {
        const scene = this.scenes[this.currentIndex];
        viewer.lookAt(scene.pitch, scene.yaw, scene.hfov, false);
      }
    }

    fail() {
      this.cleanup();
      this.frame.classList.remove('is-ready');
      this.frame.setAttribute('aria-busy', 'false');
      this.mount.tabIndex = -1;
      this.preview.hidden = this.previewFailed || !this.scenes[this.currentIndex].preview;
      this.status.textContent = this.words.error;
      this.retry.hidden = false;
      this.controlButtons.forEach((item) => { item.disabled = true; });
      this.dispatchEvent(new CustomEvent('k2-panorama:error', { bubbles: true }));
    }

    cleanup() {
      this.sequence += 1;
      clearTimeout(this.renderTimer);
      this.request?.abort();
      this.request = null;
      if (this.viewer) { this.viewer.destroy(); this.viewer = null; }
      if (this.objectURL) { URL.revokeObjectURL(this.objectURL); this.objectURL = null; }
      this.mount?.replaceChildren();
    }

    close(restoreFocus = true) {
      this.cleanup();
      if (this.dialog?.open) this.dialog.close();
      if (restoreFocus && this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
    }
  }
  customElements.define('k2-panorama', K2Panorama);
})();
